import React, { useEffect, useState } from 'react';
import api from '../utils/api';

// ── Spec-exact color tokens (DESIGN.md) ──────────────────────────────
const T = {
  surface:               '#1e100e',
  surfaceDim:            '#1e100e',
  surfaceBright:         '#473533',
  surfaceContainerLowest:'#180a09',
  surfaceContainerLow:   '#271816',
  surfaceContainer:      '#2b1c1a',
  surfaceContainerHigh:  '#372624',
  surfaceContainerHighest:'#42302f',
  onSurface:             '#f9dcd9',
  onSurfaceVariant:      '#e4beba',
  outline:               '#aa8986',
  outlineVariant:        '#5b403e',
  primary:               '#ffb3ad',
  onPrimary:             '#68000a',
  primaryContainer:      '#ff5450',
  onPrimaryContainer:    '#5c0008',
  secondary:             '#e6c448',
  onSecondary:           '#3b2f00',
  secondaryContainer:    '#ac8e0d',
  errorContainer:        '#93000a',
  surfaceVariant:        '#42302f',
};

// Inject spec-exact fonts + hazard stripe + Material Symbols on page mount
function useVaultAssets() {
  useEffect(() => {
    const links = [
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Inter:wght@400;500;600&display=swap',
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap',
    ];
    const nodes = links.map(href => {
      const l = document.createElement('link');
      l.rel = 'stylesheet'; l.href = href;
      document.head.appendChild(l);
      return l;
    });
    if (!document.getElementById('vault-spec-css')) {
      const s = document.createElement('style');
      s.id = 'vault-spec-css';
      s.textContent = `
        .v-hazard {
          background: repeating-linear-gradient(45deg, ${T.secondary}, ${T.secondary} 10px, #000 10px, #000 20px);
        }
        .v-mat {
          font-family: 'Material Symbols Outlined';
          font-weight: normal; font-style: normal;
          line-height: 1; letter-spacing: normal;
          text-transform: none; display: inline-block;
          white-space: nowrap; word-wrap: normal; direction: ltr;
          font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
        }
        .v-mat.fill { font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24; }
        .v-body { font-family: 'Inter', sans-serif; }
        .v-mono { font-family: 'Space Grotesk', monospace; }
        .v-h1 { font-family: 'Space Grotesk'; font-size: 48px; font-weight: 700; line-height: 1.1; letter-spacing: -0.02em; }
        .v-h2 { font-family: 'Space Grotesk'; font-size: 32px; font-weight: 700; line-height: 1.2; letter-spacing: -0.01em; }
        .v-h3 { font-family: 'Space Grotesk'; font-size: 24px; font-weight: 600; line-height: 1.3; }
        .v-body-text { font-family: 'Inter'; font-size: 16px; font-weight: 400; line-height: 1.6; }
        .v-mono-body { font-family: 'Space Grotesk'; font-size: 14px; font-weight: 500; line-height: 1.5; letter-spacing: 0.02em; }
        .v-label-bold { font-family: 'Space Grotesk'; font-size: 12px; font-weight: 700; line-height: 1; }
        .v-pulse:active { transform: translateY(2px); border-bottom-width: 0px !important; }
      `;
      document.head.appendChild(s);
    }
    return () => { 
      nodes.forEach(n => { 
        try { 
          document.head.removeChild(n); 
        } catch (e) {
          console.warn('Failed to remove vault stylesheet:', e);
        } 
      }); 
    };
  }, []);
}

const Mat = ({ name, fill = false, style = {} }) => (
  <span className={`v-mat ${fill ? 'fill' : ''}`} style={style}>{name}</span>
);

// ────────────────────────────────────────────────────────────────────
//  TopAppBar — literal port of <header>
// ────────────────────────────────────────────────────────────────────
function TopAppBar() {
  return (
    <header style={{ background: '#000', color: '#dc2626', borderBottom: '2px solid #1f2937', position: 'sticky', top: 0, zIndex: 50 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '16px 24px', maxWidth: 1280, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Mat name="security" style={{ color: '#dc2626', fontSize: 24 }} />
          <h1 className="v-mono" style={{ letterSpacing: '-0.02em', textTransform: 'uppercase', fontWeight: 700, fontSize: 18, margin: 0 }}>WHISTLEBLOWER_VAULT</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <nav style={{ display: 'flex', gap: 32 }} className="v-mono">
            <a href="/vault" style={navLinkStyle('#dc2626')}>SECURE_VAULT</a>
            <a href="#redaction" style={navLinkStyle(T.secondary, true)}>REDACT_PROTOCOLS</a>
            <a href="/vault/inbox" style={navLinkStyle('#6b7280')}>ENCRYPTED_COMMS</a>
          </nav>
          <Mat name="lock_reset" style={{ color: '#dc2626', fontSize: 22 }} />
        </div>
      </div>
    </header>
  );
}
const navLinkStyle = (color, active = false) => ({
  color: active ? color : '#6b7280',
  fontSize: 13, letterSpacing: '0.15em',
  fontWeight: active ? 700 : 400,
  textDecoration: 'none', padding: '4px 8px',
});

// ────────────────────────────────────────────────────────────────────
//  Main Page — literal port of <main>
// ────────────────────────────────────────────────────────────────────
export default function VaultPage() {
  useVaultAssets();
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [tos, setTos] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tos) { alert('You must agree to the indemnification clause to submit.'); return; }
    if (description.trim().length < 20) { alert('Please provide a description of at least 20 characters.'); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('description', description.trim());
      form.append('tos_agreed', 'true');
      if (file) form.append('file', file);
      const res = await api.post('/vault/submit', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      alert(err?.response?.data?.detail || 'Submission failed.');
    } finally { setSubmitting(false); }
  };

  const destroy = () => {
    if (!window.confirm('DESTROY_SESSION will clear this form and reload. Continue?')) return;
    setDescription(''); setFile(null); setTos(false);
    window.location.href = '/vault';
  };
  const copyKey = () => {
    navigator.clipboard.writeText(result?.recovery_key || '');
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  // ── POST-SUBMIT VIEW ────────────────────────────────────────────────
  if (result) {
    return (
      <div style={{ background: '#000', color: T.onSurface, minHeight: '100vh' }} className="v-body">
        <TopAppBar />
        <main style={{ maxWidth: 720, margin: '0 auto', padding: '48px 24px' }}>
          <div className="v-hazard" style={{ height: 8 }} />
          <div style={{ border: `4px solid ${T.errorContainer}`, background: '#000' }}>
            <div className="v-hazard" style={{ height: 48, display: 'flex', alignItems: 'center', padding: '0 24px', borderBottom: '2px solid #000' }}>
              <span className="v-mono v-label-bold" style={{ background: '#000', color: '#fff', padding: '6px 12px' }}>
                SUBMISSION_RECEIVED — SAVE THIS NOW
              </span>
            </div>
            <div style={{ padding: 32 }}>
              <div style={{ marginBottom: 24 }}>
                <span className="v-mono" style={{ color: T.onSurfaceVariant, fontSize: 11, letterSpacing: '0.15em' }}>YOUR IDENTIFIER</span>
                <div style={{ border: `2px solid ${T.secondary}`, background: '#000', padding: '14px 18px', marginTop: 8 }}>
                  <span className="v-mono" style={{ color: T.secondary, fontWeight: 700, fontSize: 24 }}>{result.identifier}</span>
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <span className="v-mono" style={{ color: T.onSurfaceVariant, fontSize: 11, letterSpacing: '0.15em' }}>RECOVERY KEY · 24 CHARS</span>
                <div style={{ border: `2px solid ${T.primaryContainer}`, background: '#000', padding: '14px 18px', marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="v-mono" style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{result.recovery_key}</span>
                  <button onClick={copyKey} className="v-mono" style={{ background: keyCopied ? T.secondary : 'transparent', color: keyCopied ? '#000' : T.secondary, border: `1px solid ${T.secondary}`, padding: '6px 14px', fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', cursor: 'pointer' }}>
                    {keyCopied ? 'COPIED' : 'COPY'}
                  </button>
                </div>
              </div>
              <div style={{ background: '#200000', border: `2px solid ${T.primaryContainer}`, padding: 16, marginBottom: 24, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <Mat name="warning" fill style={{ color: T.primaryContainer, fontSize: 28 }} />
                <div className="v-body-text" style={{ fontSize: 13, color: T.onSurfaceVariant }}>
                  <strong style={{ color: T.primary }}>CRITICAL.</strong> We do not store your recovery key — only an SHA-256 hash. If lost, your submission becomes permanently inaccessible. Save it offline now.
                </div>
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <a href="/vault/inbox" className="v-mono v-pulse" style={{ flex: 1, textAlign: 'center', background: T.secondary, color: '#000', padding: '14px 0', fontWeight: 700, fontSize: 13, letterSpacing: '0.15em', textDecoration: 'none', borderBottom: '4px solid #000' }}>
                  ACCESS_INBOX →
                </a>
                <button onClick={() => { setResult(null); setDescription(''); setFile(null); setTos(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  className="v-mono"
                  style={{ flex: 1, background: 'transparent', border: '2px solid #fff', color: '#fff', padding: '14px 0', fontWeight: 700, fontSize: 13, letterSpacing: '0.15em', cursor: 'pointer' }}>
                  NEW_SUBMISSION
                </button>
              </div>
            </div>
          </div>
          <div className="v-hazard" style={{ height: 8 }} />
        </main>
      </div>
    );
  }

  // ── MAIN VAULT PAGE — literal port of code.html ─────────────────────
  return (
    <div style={{ background: '#000', color: T.onSurface, minHeight: '100vh' }} className="v-body">
      <TopAppBar />

      <main style={{ maxWidth: 1280, margin: '0 auto', paddingTop: 128, paddingLeft: 32, paddingRight: 32, paddingBottom: 128 }}>

        {/* ── Hero Information Cluster (Main Intro) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 24, marginBottom: 32 }}>
          <div style={{ gridColumn: 'span 8', background: T.surfaceContainerLow, padding: 32, border: '2px solid #1f2937', borderLeft: `8px solid ${T.secondary}`, backdropFilter: 'blur(12px)' }}>
            <h2 className="v-h1" style={{ color: T.onSurface, textTransform: 'uppercase', letterSpacing: '-0.02em', marginBottom: 16 }}>
              TRANSPARENCY THROUGH ANONYMITY
            </h2>
            <p className="v-body-text" style={{ color: T.onSurfaceVariant, maxWidth: 640, marginBottom: 32 }}>
              You are connected via a cryptographically secured tunnel. This vault is engineered to facilitate the transfer of sensitive data while maintaining zero traces of your identity. Your safety is prioritized through automated metadata scrubbing and end-to-end encryption.
            </p>
            <div style={{ marginTop: 32, display: 'flex', gap: 16 }}>
              <a href="#redaction" onClick={(e) => { e.preventDefault(); document.getElementById('redaction')?.scrollIntoView({ behavior: 'smooth' }); }} style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.secondary, borderBottom: `1px solid ${T.secondary}`, padding: '4px 8px', textDecoration: 'none', fontSize: 14, fontFamily: "'Space Grotesk', monospace", fontWeight: 500, letterSpacing: '0.02em' }}>
                <Mat name="visibility_off" style={{ fontSize: 18 }} />
                HOW TO REDACT GUIDE
              </a>
            </div>
          </div>
          <div style={{ gridColumn: 'span 4', background: T.surfaceContainerLow, padding: 24, border: '2px solid #1f2937', backdropFilter: 'blur(12px)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontFamily: "'Space Grotesk', monospace", fontSize: 12, fontWeight: 700, color: '#71717a' }}>
                <span>CONNECTION STATUS</span>
                <span style={{ color: T.secondary }}>ENCRYPTED</span>
              </div>
              <div style={{ width: '100%', background: '#18181b', height: 4 }}>
                <div style={{ background: T.secondary, height: '100%', width: '88%' }} />
              </div>
            </div>
            <div style={{ marginTop: 16 }}>
              <p className="v-mono" style={{ color: '#71717a', fontSize: 10, lineHeight: 1.5 }}>
                PROTOCOL: V2-WHISTLE-S7<br />
                NODE_EXIT: ONION_ROUTING<br />
                HASH: SHA-512_SECURE
              </p>
            </div>
            <div className="v-hazard" style={{ height: 32, width: '100%', marginTop: 16 }} />
          </div>
        </div>

        {/* ── Submission Core Section ── */}
        <section style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 24 }}>
          <div style={{ gridColumn: 'span 7', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: T.surfaceContainer, border: '2px solid #1f2937' }}>
              <div style={{ background: '#1f2937', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Space Grotesk', monospace", fontSize: 12, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
                <Mat name="description" style={{ fontSize: 18 }} />
                Information / Report
              </div>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                data-testid="vault-description-input"
                placeholder="Enter the critical information here. Be specific. Avoid identifying details if you wish to remain anonymous. Document dates, names, and locations within the scope of your report."
                style={{ width: '100%', background: '#000', border: 'none', color: T.onSurface, fontFamily: "'Space Grotesk', monospace", padding: 24, minHeight: 300, resize: 'vertical', outline: 'none', fontSize: 14, lineHeight: 1.5, letterSpacing: '0.02em' }}
                onFocus={e => { e.target.style.boxShadow = `inset 0 0 0 2px ${T.secondary}`; }}
                onBlur={e => { e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div style={{ background: T.surfaceContainer, border: '2px solid #1f2937', padding: 24, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '2px dashed #3f3f46', padding: '48px 24px', background: '#000', cursor: 'pointer' }}
                onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = T.secondary; }}
                onDragLeave={e => { e.currentTarget.style.borderColor = '#3f3f46'; }}
                onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#3f3f46'; const files = e.dataTransfer.files; if (files.length > 0) setFile(files[0]); }}
                onClick={() => document.getElementById('file-input-vault')?.click()}
              >
                {file ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <span className="v-mono" style={{ color: T.secondary, fontSize: 14 }}>{file.name} · {(file.size / 1024).toFixed(1)} KB</span>
                    <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }} className="v-mono" style={{ background: 'transparent', border: `1px solid ${T.primaryContainer}`, color: T.primaryContainer, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}>REMOVE</button>
                  </div>
                ) : (
                  <>
                    <Mat name="upload_file" style={{ color: '#52525b', fontSize: 56, marginBottom: 16 }} />
                    <p className="v-mono" style={{ color: T.onSurface, textTransform: 'uppercase', fontWeight: 700, fontSize: 14, margin: '0 0 8px' }}>DRAG AND DROP EVIDENCE FILES</p>
                    <p className="v-mono" style={{ color: '#ef4444', fontSize: 10, letterSpacing: '0.15em', margin: 0 }}>CRITICAL: ALL METADATA WILL BE SCRUBBED UPON UPLOAD</p>
                    <input id="file-input-vault" type="file" data-testid="vault-file-input" accept="image/jpeg,image/png,image/webp,image/tiff,image/bmp,application/pdf" onChange={e => setFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                  </>
                )}
              </div>
            </div>

            <div style={{ background: T.surfaceContainerLow, border: '2px solid #1f2937', padding: 24 }}>
              <label style={{ display: 'flex', gap: 16, cursor: 'pointer', alignItems: 'flex-start' }}>
                <input id="tos" type="checkbox" data-testid="vault-tos-checkbox" checked={tos} onChange={e => setTos(e.target.checked)} style={{ marginTop: 4, width: 24, height: 24, border: '2px solid #3f3f46', background: '#000', accentColor: '#dc2626', flexShrink: 0 }} />
                <span className="v-mono" style={{ fontSize: 11, color: T.onSurfaceVariant, lineHeight: 1.5 }}>
                  I understand that by submitting this information, I am acting of my own volition. I agree to indemnify and hold harmless the SECURE_VAULT operators from any legal ramifications. I acknowledge that the <span style={{ color: '#ef4444', fontWeight: 700 }}>KILL SWITCH</span> policy applies: all data is permanently purged 30 days after submission unless legal intervention is requested via valid PGP-signed warrant.
                </span>
              </label>
            </div>
          </div>

          <div style={{ gridColumn: 'span 5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: T.surfaceContainerLow, padding: 48, border: '4px solid #1f2937', backdropFilter: 'blur(12px)', overflow: 'hidden', position: 'relative' }}>
            <div className="v-hazard" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 8 }} />
            <div className="v-hazard" style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 8 }} />
            <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 24 }}>
              <p className="v-mono" style={{ color: '#71717a', fontSize: 12, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Emergency Payload Execution</p>
              
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !tos || description.trim().length < 20}
                data-testid="vault-submit-button"
                className="v-pulse"
                style={{ position: 'relative', cursor: (tos && description.trim().length >= 20 && !submitting) ? 'pointer' : 'not-allowed', background: 'transparent', border: 'none', padding: 0 }}
              >
                <div style={{ position: 'absolute', inset: -16, background: 'rgba(153, 0, 19, 0.2)', borderRadius: '50%', filter: 'blur(40px)', opacity: (tos && description.trim().length >= 20) ? 1 : 0.3 }} />
                <div style={{ width: 256, height: 256, borderRadius: '50%', background: '#000', border: '8px solid #1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, position: 'relative', transition: 'transform 0.15s' }}>
                  <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: (tos && description.trim().length >= 20) ? '#dc2626' : '#3a1f1d', borderBottom: '8px solid #450a0a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: (tos && description.trim().length >= 20) ? '0 10px 0 0 #450a0a' : 'none', transition: 'all 0.15s' }}>
                    <span className="v-h3" style={{ color: (tos && description.trim().length >= 20) ? '#000' : '#71717a', fontWeight: 900, textTransform: 'uppercase', textAlign: 'center', lineHeight: 1, fontSize: 20 }}>
                      {submitting ? 'ENCRYPTING...' : 'BLOW THE\nWHISTLE'}
                    </span>
                  </div>
                </div>
              </button>

              <div style={{ paddingTop: 32, textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <p className="v-mono v-label-bold" style={{ color: '#ef4444', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}>WARNING: THIS ACTION IS IRREVERSIBLE</p>
                <p className="v-body-text" style={{ fontSize: 12, color: '#71717a' }}>A unique identification hash and recovery key will be generated upon successful submission. Record it immediately.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── Visual Context Image ── */}
        <div style={{ marginTop: 32, border: '2px solid #1f2937', height: 256, width: '100%', overflow: 'hidden', filter: 'grayscale(1) contrast(1.25)', opacity: 0.5 }}>
          <img
            alt="Cyber Security Terminal"
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuAelilYhNdxexXJSdAJkJM6ccKpC8PBK5Od83Aw04ogEhPqvge2oVZZK9nbKnP1UBHPipgHWAfd1ZBnnPkztr1y-7IXEPmYbnW_oY9zZ2zeTaJm9Y8fHGroR4skc57NqgcmiZ7Sp-z_uV3jKzlPfcISJ1SNjiAwRV95MCIblKkRUtCw2Co_vn0EJVUIpZ9klQZAGCI_ljDbrFIPO3C0Wy-WLkC9Sx-PsIGwjnVmTnF0IupoUc5y2Zvt8G_g-hpm9E045RugKSXYZvz-"
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>

        {/* ── REDACTION MANUAL (Moved below, collapsible) ── */}
        <section id="redaction" style={{ marginTop: 64, marginBottom: 32, border: '2px solid #1f2937', background: T.surfaceContainerLow, padding: 24, position: 'relative', overflow: 'hidden' }}>
          <div className="v-hazard" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 4 }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
            <div style={{ flex: '2 1 360px', minWidth: 0 }}>
              <div className="v-mono v-label-bold" style={{ display: 'inline-block', background: T.secondary, color: '#000', padding: '4px 8px', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: 16 }}>
                Classification: Top Secret
              </div>
              <h2 className="v-h1" style={{ color: T.onSurface, textTransform: 'uppercase', margin: '0 0 16px' }}>Redaction_Manual_v4.0</h2>
              <p className="v-body-text" style={{ color: T.onSurfaceVariant, maxWidth: 640, margin: '0 0 16px' }}>
                Information leakage is the primary cause of asset compromise. Use this guide to ensure that all evidence submitted to the vault is fully sterilized of metadata, temporal markers, and identifying visual patterns.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                <span className="v-mono v-mono-body" style={{ border: `1px solid ${T.secondary}`, color: T.secondary, padding: '4px 8px' }}>OPSEC_READY</span>
                <span className="v-mono v-mono-body" style={{ border: `1px solid ${T.primary}`, color: T.primary, padding: '4px 8px' }}>NO_TRACE_PROTOCOL</span>
              </div>
            </div>
            <div style={{ flex: '1 1 240px', aspectRatio: '1 / 1', background: '#111827', border: '2px solid #1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              <img
                alt="A gritty, cinematic close-up of a high-tech server room bathed in dramatic red and amber lighting."
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuCw3YxqCT7tEj1q2OA9yvvB7w1lVLvgEi4hRBPJw5XYmwy2vxHt9OUnLNnEZYxxdl3UGxish2Ixvjd6xqciDWgasrrgliZ3M-sGAcYF6G3iFvDaLOrR7Yqb0Mq1x9XW9ORqzFKAB9-npK_p0OaVr0R55OjMYj0ctCLTGy8j0sGfDEg5j3og8ZJln8txGNOlC498F3zEqCjuyCqMaJVW7j6dzGNcQn2_S0oq5FlrBdvJLPxnXtbpTWQ3nAgedXyXhgZty3YL7QjZrhD3"
                style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'grayscale(1) contrast(1.25)', opacity: 0.6 }}
              />
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Mat name="visibility_off" fill style={{ color: T.primary, fontSize: 96 }} />
              </div>
            </div>
          </div>
        </section>

        {/* ── Bento Grid Protocols (Redaction Details) ── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 24, marginBottom: 64 }}>

          {/* Metadata Scrubbing — col-span-8 */}
          <div style={{ gridColumn: 'span 8 / span 8', border: '2px solid #1f2937', background: T.surfaceContainer, overflow: 'hidden' }}>
            <div className="v-h3" style={{ background: T.secondary, color: '#000', padding: '8px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>01_METADATA_EXTIRPATION</span>
              <Mat name="cleaning_services" />
            </div>
            <div style={{ padding: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
              <div>
                <h4 className="v-h3" style={{ color: T.secondary, margin: '0 0 12px' }}>Digital Fingerprints</h4>
                <p className="v-body-text" style={{ color: T.onSurfaceVariant, margin: '0 0 16px' }}>
                  Every digital file contains "Exif" or "XMP" data. This includes GPS coordinates, camera serial numbers, and software versions used for editing.
                </p>
                <div style={{ background: '#000', padding: 14, borderLeft: '4px solid #dc2626' }}>
                  <ul className="v-mono v-mono-body" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                      <Mat name="dangerous" style={{ color: '#ef4444', fontSize: 16 }} /><span>REMOVE: GPS Lat/Long</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
                      <Mat name="dangerous" style={{ color: '#ef4444', fontSize: 16 }} /><span>REMOVE: Device UID</span>
                    </li>
                    <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                      <Mat name="dangerous" style={{ color: '#ef4444', fontSize: 16 }} /><span>REMOVE: Creation Timestamp</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div style={{ border: '2px solid #1f2937', padding: 14, background: '#000', position: 'relative' }}>
                <div style={{ aspectRatio: '16 / 9', background: '#111827', border: '1px solid #374151', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: 16 }}>
                  <img
                    alt="A technical visualization of raw data packets flowing through a dark network environment."
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuA_oK-hqkvTCPp4o__KNRypDrPTO5t0DnZhe7yrs9LJaezQS77mpuGpUv6aNp-P4oXA_5PD3i7h48tHDMbsjkfEv-30opWeJcE58MzlhTbNzeh3oDTorsnXYM55Zjm3xO70L3nu3TnUSVtVebPMJC3rBBtz2P_vNxN0p2iycKv-775_LKYigPwoVs5qYOBEJinM-WugvAjGaNsTn-oeMer9gcOcPQey4Fsw6zwgd4gLmja3NSjSKRpXsTsHquEGHTaFeyt1BFePBgES"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.4 }}
                  />
                </div>
                <div className="v-mono" style={{ display: 'block', width: '100%', padding: '16px 0', background: T.secondary, color: '#000', textAlign: 'center', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.15em' }}>
                  AUTO_SCRUB_ACTIVE
                </div>
              </div>
            </div>
          </div>

          {/* Redaction Types — col-span-4 */}
          <div style={{ gridColumn: 'span 4 / span 4', border: '2px solid #1f2937', background: T.surfaceContainer, display: 'flex', flexDirection: 'column' }}>
            <div className="v-h3" style={{ background: '#1f2937', color: T.onSurface, padding: '8px 24px' }}>02_REDACTION_MODES</div>
            <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ padding: 14, border: '2px solid #dc2626', background: '#000' }}>
                <h5 className="v-mono v-label-bold" style={{ color: '#ef4444', margin: '0 0 6px' }}>PHYSICAL_METHOD</h5>
                <p className="v-mono" style={{ fontSize: 12, lineHeight: 1.5, color: T.onSurfaceVariant, opacity: 0.85, margin: 0 }}>
                  Hard-copy blackout using industrial ink. Scanning then re-printing is mandatory to prevent indentation recovery.
                </p>
              </div>
              <div style={{ padding: 14, border: `2px solid ${T.secondary}`, background: '#000' }}>
                <h5 className="v-mono v-label-bold" style={{ color: T.secondary, margin: '0 0 6px' }}>DIGITAL_METHOD</h5>
                <p className="v-mono" style={{ fontSize: 12, lineHeight: 1.5, color: T.onSurfaceVariant, opacity: 0.85, margin: 0 }}>
                  Vector-level path removal. Do NOT use "black highlight" tools which can be removed in PDF viewers. Flatten all layers.
                </p>
              </div>
              <div style={{ marginTop: 'auto', paddingTop: 16 }}>
                <div className="v-hazard" style={{ height: 32, width: '100%', border: '2px solid #1f2937' }} />
              </div>
            </div>
          </div>

          {/* Location Neutrality — col-span-12 */}
          <div style={{ gridColumn: 'span 12 / span 12', border: '2px solid #1f2937', background: T.surfaceContainerLow, padding: 24 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
              <div style={{ flex: '1 1 360px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Mat name="map" fill style={{ color: T.secondary, fontSize: 28 }} />
                  <h3 className="v-h2" style={{ color: T.onSurface, textTransform: 'uppercase', margin: 0 }}>Neutrality_Check</h3>
                </div>
                <p className="v-body-text" style={{ color: T.onSurfaceVariant, margin: '12px 0' }}>
                  Avoid capturing landmarks, unique foliage, or weather patterns that could be correlated with historical meteorological data to pinpoint your location and time of recording.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div style={{ background: '#000', border: '1px solid #1f2937', padding: 14 }}>
                    <span className="v-mono v-label-bold" style={{ display: 'block', color: '#ef4444', textTransform: 'uppercase', marginBottom: 8 }}>Identify_Risk</span>
                    <p className="v-mono" style={{ fontSize: 12, fontStyle: 'italic', color: T.onSurfaceVariant, margin: 0 }}>
                      "The shadow cast by the flagpole indicates a 2:15 PM timestamp at 42.3 degrees latitude..."
                    </p>
                  </div>
                  <div style={{ background: '#000', border: '1px solid #1f2937', padding: 14 }}>
                    <span className="v-mono v-label-bold" style={{ display: 'block', color: T.secondary, textTransform: 'uppercase', marginBottom: 8 }}>Counter_Measure</span>
                    <p className="v-mono" style={{ fontSize: 12, fontStyle: 'italic', color: T.onSurfaceVariant, margin: 0 }}>
                      "Crop all horizon lines and non-essential architectural features. Use high-contrast filters to obscure shadow gradients."
                    </p>
                  </div>
                </div>
              </div>
              <div style={{ flex: '1 1 360px', border: '2px solid #1f2937', background: '#000', padding: 4, position: 'relative', minHeight: 240 }}>
                <div style={{ position: 'absolute', inset: 0, filter: 'grayscale(1) contrast(1.5) brightness(0.5)' }}>
                  <img
                    alt="A satellite-view map of a sprawling urban landscape at night, heavily processed with digital artifacts."
                    src="https://lh3.googleusercontent.com/aida-public/AB6AXuCTTrUG6HJfzcuEwRtkafRQYKuXwU8LcaUaOBctZfVW9Inc_k9Cc7TRANd53DU3Un6JTjMe2sP6eKpl0R66Lsk0JEOuut818Kll1dyxV7S1jUj96nH02lgsLm6jwRpWOTjsDLr-2TWsB-XaSivTrcOYyJni8OPOMQ9sdK0o-JH4plGCL1tanvoTaygdlP0al9wm1rYiPUyhWxRmze0XsmACTJ5upiH6iTAJG7RyTI3gN3xU5oe8ffjmAUkW1RULgvHNoei1FlKCfgk3"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
                <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: 24, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', border: '2px dashed #ef4444', margin: 8 }}>
                  <Mat name="location_off" style={{ color: '#ef4444', fontSize: 56, marginBottom: 16 }} />
                  <span className="v-h3" style={{ color: '#fff', textTransform: 'uppercase', letterSpacing: '0.2em', textAlign: 'center', margin: 0 }}>
                    ANONYMITY_SHIELD_ACTIVE
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Final Security Checklist — col-span-12 */}
          <div style={{ gridColumn: 'span 12 / span 12' }}>
            <div style={{ background: T.errorContainer, border: '2px solid #1f2937', padding: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <Mat name="gavel" fill style={{ color: '#fff', fontSize: 32 }} />
                <h3 className="v-h2" style={{ color: '#fff', textTransform: 'uppercase', margin: 0, letterSpacing: '-0.02em' }}>FINAL_SECURITY_CHECKLIST</h3>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
                {[
                  { t: 'Symmetry_Test', b: 'Ensure no reflections are visible in window panes, eye retinas, or metallic surfaces. These are common points of failure.' },
                  { t: 'Temporal_Gaps', b: 'If documenting a series of events, vary the upload timing to prevent pattern analysis of your active hours.' },
                  { t: 'Vocal_Anonymity', b: 'For audio/video, use hardware-based voice distortion. Software filters can often be reversed with AI spectral analysis.' },
                ].map(it => (
                  <div key={it.t} style={{ background: 'rgba(0,0,0,0.2)', padding: 14, border: '1px solid rgba(255,255,255,0.2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ width: 8, height: 8, background: T.secondary }} />
                      <span className="v-mono v-label-bold" style={{ color: '#fff', textTransform: 'uppercase' }}>{it.t}</span>
                    </div>
                    <p className="v-body-text" style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: 0 }}>{it.b}</p>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'center' }}>
                <a href="/terms" className="v-mono v-mono-body" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: '1px solid #fff', color: '#fff', padding: '8px 24px', textTransform: 'uppercase', letterSpacing: '0.15em', textDecoration: 'none' }}>
                  <Mat name="description" style={{ fontSize: 16 }} />
                  VIEW FULL SECURITY PROTOCOL
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer + Kill Switch Policy ── */}
        <footer style={{ marginTop: 32, borderTop: '4px solid #1f2937', background: '#000', padding: 48, textAlign: 'center' }}>
          <div style={{ maxWidth: 768, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: T.secondary }}>
              <Mat name="timer" />
              <h4 className="v-h3" style={{ textTransform: 'uppercase', letterSpacing: '-0.02em', margin: 0 }}>30-DAY KILL SWITCH POLICY</h4>
            </div>
            <p className="v-mono v-mono-body" style={{ color: '#71717a', fontSize: 14 }}>
              To protect our sources, the SECURE_VAULT operates on a strict non-persistence architecture. 720 hours after your submission, the encrypted container is shredded using Department of Defense standards (DoD 5220.22-M). No backups exist. No logs are kept. No metadata survives.
            </p>
            <p className="v-mono" style={{ color: '#3f3f46', fontSize: 10, paddingTop: 32 }}>
              CYQR_VAULT_REVISION_6.2.0 // DEPLOYED_AT: 2023.10.12 // STATUS: HARDENED
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}
